import { useEffect, useState, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './useAuth';
import type { NavNode, PageType, BlockType } from '../types/nav';
import {
  addSection as svcAddSection,
  addPage as svcAddPage,
  addBlock as svcAddBlock,
  movePageToBlock as svcMovePageToBlock,
  updateNode as svcUpdateNode,
  deleteNode as svcDeleteNode,
  reorderNodes as svcReorderNodes,
} from '../services/navTreeService';

const COLLECTION = 'demo_portal_nav';

export function useNavTree() {
  const { user } = useAuth();
  const [nodes, setNodes] = useState<NavNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, COLLECTION),
      orderBy('order', 'asc'),
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(
          (d) => ({ id: d.id, ...d.data() } as NavNode),
        );
        setNodes(items);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Nav tree subscription error:', err);
        setError(err.message);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  const sections = nodes.filter((n) => n.type === 'section');
  const pages = nodes.filter((n) => n.type === 'page');
  const blocks = nodes.filter((n) => n.type === 'block');

  const uid = user?.uid ?? '';

  const addSection = useCallback(
    async (title: string, icon: string) => {
      const nextOrder = sections.length;
      await svcAddSection(title, icon, uid, nextOrder);
    },
    [uid, sections.length],
  );

  const addPage = useCallback(
    async (
      parentId: string,
      title: string,
      url: string,
      pageType: PageType,
      cardId?: string,
      blockId?: string,
    ) => {
      // Order among siblings in the same container (block or ungrouped).
      const sibling = pages.filter(
        (p) => p.parentId === parentId && (blockId ? p.blockId === blockId : !p.blockId),
      );
      const nextOrder = sibling.length;
      await svcAddPage(parentId, title, url, pageType, uid, nextOrder, cardId, blockId);
    },
    [uid, pages],
  );

  const updateNode = useCallback(
    async (
      id: string,
      updates: Partial<Pick<NavNode, 'title' | 'icon' | 'url' | 'pageType' | 'order'>>,
    ) => {
      await svcUpdateNode(id, updates, uid);
    },
    [uid],
  );

  const deleteNode = useCallback(
    async (id: string) => {
      await svcDeleteNode(id);
    },
    [],
  );

  const reorderNodes = useCallback(
    async (nodeIds: string[]) => {
      await svcReorderNodes(nodeIds, uid);
    },
    [uid],
  );

  const addBlock = useCallback(
    async (parentId: string, blockType: BlockType, title: string) => {
      const siblings = blocks.filter((b) => b.parentId === parentId);
      const nextOrder = siblings.length;
      return svcAddBlock(parentId, blockType, title, uid, nextOrder);
    },
    [uid, blocks],
  );

  const movePageToBlock = useCallback(
    async (pageId: string, blockId: string | null) => {
      await svcMovePageToBlock(pageId, blockId, uid);
    },
    [uid],
  );

  return {
    sections,
    pages,
    blocks,
    loading,
    error,
    addSection,
    addPage,
    addBlock,
    movePageToBlock,
    updateNode,
    deleteNode,
    reorderNodes,
  };
}
